import React, { useEffect, useState } from "react";
import dreamschoolsLogo from "../../assets/img/redesign-logo.webp";
import "./BillingDayChangeConfirmation.css";

const endpoint = `${process.env.REACT_APP_API}/partners/billing-day-changes/public`;

function formatBillingDay(day) {
  const lastTwo = day % 100;
  const suffix = lastTwo >= 11 && lastTwo <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" }[day % 10] || "th");
  return `${day}${suffix}`;
}

// Use fetch rather than the app's Axios instance: its telemetry interceptor
// includes the full page URL in a custom referrer header.
async function post(path, body) {
  const response = await fetch(`${endpoint}/${path}`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body), referrerPolicy: "no-referrer", cache: "no-store"
  });
  const result = await response.json();
  if (!response.ok || !result.success) {
    const error = new Error(result.message || "Unable to load the billing change. Please try again.");
    error.status = response.status;
    throw error;
  }
  return result.data;
}

export default function BillingDayChangeConfirmation() {
  const [token] = useState(() => {
    const value = new URLSearchParams(window.location.hash.slice(1)).get("token");
    // Keep the link's secret only in component memory, away from analytics and
    // subsequent navigation. Reopen the email link if the page is refreshed.
    window.history.replaceState(window.history.state, "", window.location.pathname);
    return value;
  });
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    if (!token) {
      setError("Open the confirmation link in your email to review this billing change.");
      setLoading(false);
      return;
    }
    post("review", { token }).then(data => {
      if (active) setDetails(data);
    }).catch(cause => { if (active) setError(cause.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  const confirm = async () => {
    if (!details || confirming || details.status === "applied") return;
    setConfirming(true);
    setError("");
    try {
      setDetails(await post("confirm", { token, previewKey: details.previewKey }));
    } catch (cause) {
      setError(cause.message);
      if (cause.status === 409) {
        try {
          setDetails(await post("review", { token }));
        } catch (reviewError) {
          setDetails(null);
          setError(reviewError.message);
        }
      }
    } finally {
      setConfirming(false);
    }
  };

  const applied = details?.status === "applied";
  return <div className="billing-day-confirmation">
    <header className="billing-day-confirmation-header">
      <div className="billing-day-confirmation-header-content">
        <a href="/" aria-label="Dreamschools home" className="billing-day-confirmation-logo">
          <img src={dreamschoolsLogo} alt="Dreamschools" />
        </a>
        <a href="/user" className="billing-day-confirmation-dashboard-link">Parent dashboard</a>
      </div>
    </header>
    <main className="billing-day-confirmation-content">
    <section className="billing-day-confirmation-form" aria-labelledby="billing-day-confirmation-title">
      <h1 id="billing-day-confirmation-title">{applied ? "Your billing day has been updated" : "Confirm your recurring billing day"}</h1>
      {loading && <p role="status">Loading your billing change…</p>}
      {error && <p role="alert" className="billing-day-confirmation-error">{error}</p>}
      {details && <>
        <p><strong>{details.organizationName}</strong> {applied ? "has updated" : "has requested a change to"} the recurring billing day for <strong>{details.childName}</strong> in {details.programName}.</p>
        <dl className="billing-day-confirmation-details">
          {!applied && <div className="billing-day-confirmation-detail"><dt>Current billing day</dt><dd>{formatBillingDay(details.currentBillingDay)} of each month</dd></div>}
          <div className="billing-day-confirmation-detail billing-day-confirmation-new-day"><dt>{applied ? "Billing day" : "New billing day"}</dt><dd>{formatBillingDay(details.billingDay)} of each month</dd></div>
          {details.keptPaymentDate && <div className="billing-day-confirmation-detail"><dt>Next payment stays unchanged</dt><dd>{details.keptPaymentDate}</dd></div>}
          <div className="billing-day-confirmation-detail"><dt>First payment on the new schedule</dt><dd>{details.nextPaymentDate}</dd></div>
          <div className="billing-day-confirmation-detail"><dt>Following payments</dt><dd>{details.followingPaymentDates.join("; ")}</dd></div>
          <div className="billing-day-confirmation-detail"><dt>Billing timezone</dt><dd>{details.timezone}</dd></div>
        </dl>
        {applied ? <p role="status">{details.keptPaymentDate ? `Your change is confirmed. The next payment stays on ${details.keptPaymentDate}, and the new recurring day starts on ${details.nextPaymentDate}.` : "The new recurring schedule is active."} Your regular monthly price has not changed. You can close this page.</p> : <>
          {details.dateChanged && <p className="billing-day-confirmation-updated-dates" role="status">The first payment date has changed since the email was sent. Please review the dates above before confirming.</p>}
          <p className="billing-day-confirmation-notice">{details.transitionNotice}</p>
          <p className="billing-day-confirmation-consent">By selecting “I confirm”, you authorize payments on the new recurring schedule shown above.</p>
          <button type="button" onClick={confirm} disabled={confirming}>
            {confirming ? "Updating your billing day…" : "I confirm"}
          </button>
          <p className="billing-day-confirmation-help">If you do not agree, close this page and contact the organizer. Your current schedule will continue.</p>
        </>}
      </>}
    </section>
    </main>
  </div>;
}
